import React, { useState, useEffect, useCallback } from 'react';
import {
  Shuffle, List, Plus, X, Search, Loader,
  ChevronDown, ChevronUp, SkipForward, Music2
} from 'lucide-react';

export default function AdminQueuePanel({ allSongs = [], currentSongTitle = '' }) {
  const [nextSongs, setNextSongs]         = useState([]);
  const [shuffle, setShuffle]             = useState(false);
  const [totalSongs, setTotalSongs]       = useState(0);
  const [shuffleLoading, setShuffleLoading] = useState(false);
  const [skipping, setSkipping]           = useState(false);
  const [showQueue, setShowQueue]         = useState(true);
  const [showSearch, setShowSearch]       = useState(false);
  const [searchQ, setSearchQ]             = useState('');
  const [addingId, setAddingId]           = useState(null);
  const [currentId, setCurrentId]         = useState(null);

  const fetchQueue = useCallback(async () => {
    try {
      const [nxtRes, shfRes, curRes] = await Promise.all([
        fetch('/api/player/next-songs?limit=8'),
        fetch('/api/player/shuffle/status'),
        fetch('/api/player/current'),
      ]);
      if (nxtRes.ok) {
        const d = await nxtRes.json();
        setNextSongs(d.proximas || []);
        setTotalSongs(d.total || 0);
        setShuffle(d.shuffle || false);
      }
      if (shfRes.ok) {
        const d = await shfRes.json();
        setShuffle(d.shuffle || false);
        if (d.total_songs) setTotalSongs(d.total_songs);
      }
      if (curRes.ok) {
        const d = await curRes.json();
        setCurrentId(d.id || null);
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    fetchQueue();
    const iv = setInterval(fetchQueue, 8000);
    return () => clearInterval(iv);
  }, [fetchQueue]);

  const handleShuffle = async () => {
    setShuffleLoading(true);
    try {
      const res = await fetch('/api/player/shuffle/toggle', { method: 'POST' });
      if (res.ok) {
        const d = await res.json();
        setShuffle(d.shuffle);
        await fetchQueue();
      }
    } catch (e) {}
    setShuffleLoading(false);
  };

  const handleSkip = async () => {
    setSkipping(true);
    try {
      await fetch('/api/player/skip', { method: 'POST' });
      setTimeout(() => { fetchQueue(); setSkipping(false); }, 1500);
    } catch (e) { setSkipping(false); }
  };

  const handleAdd = async (song) => {
    setAddingId(song.id);
    try {
      await fetch(`/api/player/push/${song.id}`, { method: 'POST' });
      setTimeout(() => { fetchQueue(); setAddingId(null); }, 800);
    } catch (e) { setAddingId(null); }
  };

  const handleRemove = async (songId) => {
    try {
      await fetch(`/api/player/queue/remove/${songId}`, { method: 'POST' });
      setNextSongs(prev => {
        const copy = [...prev];
        const idx = copy.findIndex(s => s.id === songId);
        if (idx > -1) copy.splice(idx, 1);
        return copy;
      });
    } catch (e) {}
  };

  const fmtDur = (s) => s > 0 ? `${Math.floor(s/60)}:${(s%60).toString().padStart(2,'0')}` : '';

  const queueIds = new Set(nextSongs.map(s => s.id));
  const filtered = allSongs.filter(s =>
    s.titulo.toLowerCase().includes(searchQ.toLowerCase()) ||
    s.artista.toLowerCase().includes(searchQ.toLowerCase())
  );

  return (
    <div className="bg-black bg-opacity-40 rounded-xl border border-blue-500/40 backdrop-blur-md overflow-hidden mt-6">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-blue-500/20 bg-blue-600/5">
        <div className="flex items-center gap-2">
          <Music2 className="w-4 h-4 text-blue-400" />
          <h3 className="text-white font-black text-sm uppercase tracking-widest">Control de Reproducción</h3>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-slate-500 font-bold uppercase">
          <span>{totalSongs} canciones en biblioteca</span>
        </div>
      </div>

      <div className="p-6 space-y-5">

        {/* Canción actual + skip */}
        <div className="flex items-center justify-between gap-4 bg-green-500/5 border border-green-500/20 rounded-xl px-4 py-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex gap-0.5 items-end h-4 flex-shrink-0">
              {[1,2,3].map(i => (
                <div key={i} className="w-1 bg-green-400 rounded-sm animate-pulse"
                     style={{height:`${40+i*20}%`, animationDelay:`${i*0.15}s`}}></div>
              ))}
            </div>
            <div className="min-w-0">
              <p className="text-[9px] text-green-400 font-black uppercase tracking-widest">Sonando ahora</p>
              <p className="text-white font-bold text-sm truncate">{currentSongTitle || 'Radio ASENF'}</p>
            </div>
          </div>
          <button onClick={handleSkip} disabled={skipping}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-black text-xs uppercase tracking-wider transition flex-shrink-0">
            {skipping ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <SkipForward className="w-3.5 h-3.5" />}
            Saltar
          </button>
        </div>

        {/* Shuffle */}
        <div className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${
          shuffle
            ? 'bg-blue-600/15 border-blue-500/40'
            : 'bg-slate-800/40 border-slate-700/40'
        }`}>
          <div className="flex items-center gap-3">
            <Shuffle className={`w-4 h-4 ${shuffle ? 'text-blue-400' : 'text-slate-500'}`} />
            <div>
              <p className={`text-xs font-black uppercase tracking-wider ${shuffle ? 'text-blue-300' : 'text-slate-400'}`}>
                Reproducción aleatoria
              </p>
              <p className="text-[9px] text-slate-600">
                {shuffle
                  ? `Activo — mezcla aleatoria de ${totalSongs} canciones`
                  : 'Desactivado — orden de programación normal'}
              </p>
            </div>
          </div>
          <button onClick={handleShuffle} disabled={shuffleLoading}
            className={`relative w-11 h-6 rounded-full border transition-all flex-shrink-0 ${
              shuffle
                ? 'bg-blue-600 border-blue-500'
                : 'bg-slate-700 border-slate-600'
            }`}>
            {shuffleLoading
              ? <Loader className="w-3 h-3 text-white absolute top-1.5 left-1.5 animate-spin" />
              : <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${shuffle ? 'left-5' : 'left-0.5'}`}></span>
            }
          </button>
        </div>

        {/* Cola + Buscador tabs */}
        <div className="flex gap-2">
          <button onClick={() => { setShowQueue(true); setShowSearch(false); }}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition ${
              showQueue && !showSearch ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}>
            <List className="w-3.5 h-3.5" />
            Cola ({nextSongs.length})
          </button>
          <button onClick={() => { setShowSearch(true); setShowQueue(false); setSearchQ(''); }}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition ${
              showSearch ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}>
            <Plus className="w-3.5 h-3.5" />
            Agregar por demanda
          </button>
        </div>

        {/* COLA */}
        {showQueue && !showSearch && (
          <div>
            {nextSongs.length === 0 ? (
              <div className="text-center py-8 bg-slate-900/30 rounded-xl border border-slate-800 border-dashed">
                <List className="w-8 h-8 text-slate-700 mx-auto mb-2 opacity-40" />
                <p className="text-slate-600 text-xs font-bold uppercase tracking-widest">Cola vacía</p>
                <p className="text-slate-700 text-[10px] mt-1">Usa "Agregar por demanda" para encolar canciones</p>
              </div>
            ) : (
              <div className="space-y-1.5 max-h-64 overflow-y-auto custom-scrollbar pr-1">
                {nextSongs.map((song, idx) => (
                  <div key={`${song.id}-${idx}`}
                    className="flex items-center gap-3 bg-slate-800/50 border border-slate-700/40 rounded-lg px-3 py-2.5 group hover:bg-slate-800 transition">
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-[10px] font-black text-slate-600 w-4 text-right">{idx + 1}</span>
                      <div className="w-1 h-6 rounded-full" style={{background: idx === 0 ? '#3b82f6' : '#334155'}}></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-bold truncate">{song.titulo}</p>
                      <p className="text-[10px] text-slate-500 truncate">{song.artista}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {song.duracion > 0 && <span className="text-[10px] text-slate-600">{fmtDur(song.duracion)}</span>}
                      <button onClick={() => handleRemove(song.id)}
                        className="opacity-0 group-hover:opacity-100 w-6 h-6 text-red-400 hover:text-red-300 flex items-center justify-center rounded transition"
                        title="Quitar de la cola">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* BUSCADOR POR DEMANDA */}
        {showSearch && (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input type="text" value={searchQ} onChange={e => setSearchQ(e.target.value)}
                placeholder="Buscar canción o artista para encolar..."
                autoFocus
                className="w-full bg-slate-800 border border-slate-700 text-white text-sm pl-10 pr-4 py-2.5 rounded-xl focus:outline-none focus:border-blue-500" />
              {searchQ && (
                <button onClick={() => setSearchQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="max-h-72 overflow-y-auto space-y-1 custom-scrollbar pr-1">
              {(searchQ ? filtered : allSongs).slice(0, 25).map(song => {
                const inQueue   = queueIds.has(song.id);
                const isCurr    = song.id === currentId;
                return (
                  <div key={song.id}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition ${
                      isCurr   ? 'bg-green-500/10 border-green-500/30' :
                      inQueue  ? 'bg-blue-600/10 border-blue-500/20' :
                                 'bg-slate-800/40 border-transparent hover:bg-slate-700/60'}`}>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-bold truncate ${isCurr ? 'text-green-400' : inQueue ? 'text-blue-300' : 'text-white'}`}>
                        {isCurr && '▶ '}{song.titulo}
                      </p>
                      <p className="text-[10px] text-slate-500 truncate">{song.artista}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {song.duracion > 0 && <span className="text-[10px] text-slate-600">{fmtDur(song.duracion)}</span>}
                      {isCurr ? (
                        <span className="text-[9px] text-green-400 font-black bg-green-900/40 px-2 py-0.5 rounded border border-green-500/20">AL AIRE</span>
                      ) : inQueue ? (
                        <span className="text-[9px] text-blue-400 font-black bg-blue-900/40 px-2 py-0.5 rounded border border-blue-500/20">EN COLA</span>
                      ) : (
                        <button onClick={() => handleAdd(song)} disabled={addingId === song.id}
                          className="flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition active:scale-95 disabled:opacity-50">
                          {addingId === song.id ? <Loader className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                          Encolar
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {searchQ && filtered.length === 0 && (
                <div className="text-center py-8 text-slate-600">
                  <p className="text-sm italic">Sin resultados para "{searchQ}"</p>
                </div>
              )}
              {!searchQ && allSongs.length === 0 && (
                <div className="text-center py-8 text-slate-600 text-xs">Cargando biblioteca...</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
